import type { ChargingProfileSnapshot } from '@/state/types';
import { ProfilePurpose } from '@/state/types';

const PURPOSE_BADGE: Record<string, string> = {
  [ProfilePurpose.ChargePointMaxProfile]: 'bg-rose-800 text-rose-100',
  [ProfilePurpose.TxDefaultProfile]: 'bg-sky-800 text-sky-100',
  [ProfilePurpose.TxProfile]: 'bg-primary-pressed text-primary-on',
};

export function ChargingProfileTable({
  entries,
  onDelete,
}: {
  entries: readonly ChargingProfileSnapshot[];
  onDelete: (id: number) => void;
}): JSX.Element {
  if (entries.length === 0) {
    return (
      <p
        className="rounded border border-slate-700 bg-slate-900 p-4 text-xs text-slate-500"
        data-testid="profiles-empty"
      >
        No charging profiles installed. Use the buttons above to add a template.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
    <table className="w-full min-w-[40rem] text-left text-xs" data-testid="profile-table">
      <thead className="text-slate-500">
        <tr>
          <th className="px-2 py-1">id</th>
          <th className="px-2 py-1">purpose</th>
          <th className="px-2 py-1">kind</th>
          <th className="px-2 py-1">conn</th>
          <th className="px-2 py-1">stack</th>
          <th className="px-2 py-1">periods</th>
          <th className="px-2 py-1" />
        </tr>
      </thead>
      <tbody>
        {entries.map((p) => {
          const cls = PURPOSE_BADGE[p.purpose] ?? 'bg-slate-800 text-slate-200';
          return (
            <tr
              key={Number(p.id)}
              className="border-t border-slate-800"
              data-testid={`profile-row-${Number(p.id)}`}
            >
              <td className="px-2 py-1 font-mono text-slate-100">#{Number(p.id)}</td>
              <td className="px-2 py-1">
                <span className={`rounded px-2 py-0.5 text-[10px] font-medium ${cls}`}>
                  {p.purpose}
                </span>
              </td>
              <td className="px-2 py-1 text-slate-300">
                {p.kind}
                {p.recurrencyKind ? ` / ${p.recurrencyKind}` : ''}
              </td>
              <td className="px-2 py-1 text-slate-300">
                {p.connectorId === 0 ? 'station' : p.connectorId}
              </td>
              <td className="px-2 py-1 text-slate-300">{p.stackLevel}</td>
              <td className="px-2 py-1 font-mono text-slate-400">
                {p.schedule.chargingSchedulePeriod
                  .map(
                    (period) =>
                      `${period.startPeriod}s→${period.limit}${p.schedule.chargingRateUnit}`,
                  )
                  .join(' · ')}
              </td>
              <td className="px-2 py-1 text-right">
                <button
                  type="button"
                  onClick={() => onDelete(Number(p.id))}
                  className="rounded bg-rose-700 px-2 py-0.5 text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
                  data-testid={`profile-delete-${Number(p.id)}`}
                >
                  Delete
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}
